import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSession } from './lib/auth';
import { useDelayedLoading } from './lib/useDelayedLoading';
import { initNativeShell } from './lib/nativeShell';
import { Login } from './pages/Login';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { Signup } from './pages/Signup';
import { Dashboard } from './pages/Dashboard';
import { Sites } from './pages/Sites';
import { SiteRegister } from './pages/SiteRegister';
import { SignInForm } from './pages/SignInForm';
import { ChecksMenu } from './pages/ChecksMenu';
import { TickListRunForm } from './pages/TickListRunForm';
import { TickListRunView } from './pages/TickListRunView';
import { FormsMenu } from './pages/FormsMenu';
import { FormTBT } from './pages/FormTBT';
import { FormAccident } from './pages/FormAccident';
import { FormSubbie } from './pages/FormSubbie';
import { FormView } from './pages/FormView';
import { Settings } from './pages/Settings';
import { SettingsFeatures } from './pages/SettingsFeatures';
import { Team } from './pages/Team';
import { AcceptInvite } from './pages/AcceptInvite';
import { CompletedJobs } from './pages/CompletedJobs';
import { Plan } from './pages/Plan';
import { Security } from './pages/Security';
import { Registers } from './pages/Registers';
import { RamsList } from './pages/RamsList';
import { RamsListView } from './pages/RamsListView';
import { RamsAdd } from './pages/RamsAdd';
import { RamsPresets } from './pages/RamsPresets';
import { RamsPresetForm } from './pages/RamsPresetForm';
import { Terms } from './pages/Terms';
import { Privacy } from './pages/Privacy';
import { RegisterPlant } from './pages/RegisterPlant';
import { RegisterCOSHH } from './pages/RegisterCOSHH';
import { RegisterTraining } from './pages/RegisterTraining';
import { RegisterSubbies } from './pages/RegisterSubbies';
import { RegisterExpiries } from './pages/RegisterExpiries';
import { RegisterFirstAiders } from './pages/RegisterFirstAiders';
import { DocumentsLibrary } from './pages/DocumentsLibrary';
import { DocumentsIssued } from './pages/DocumentsIssued';
import { FormsHistory } from './pages/FormsHistory';
import { DocumentFillForm } from './pages/DocumentFillForm';
import { CppFillForm } from './pages/CppFillForm';
import { SiteSetupChecklist } from './pages/SiteSetupChecklist';
import { Timesheet } from './pages/Timesheet';
import { SiteRecords } from './pages/SiteRecords';
import { HowToVideos } from './pages/HowToVideos';
import { SplashGate } from './components/SplashGate';
import { RequireSubscription, RequireSession } from './components/RequireSubscription';
import { Paywall } from './pages/Paywall';
import { Checkout } from './pages/Checkout';
import { PartnerProgramme } from './pages/PartnerProgramme';
import { CloseSite } from './pages/CloseSite';
import { OpenIssues } from './pages/OpenIssues';
import { Welcome } from './pages/Welcome';
import { WelcomeFirstRams } from './pages/WelcomeFirstRams';
import { FridayWrapUp } from './pages/FridayWrapUp';
import { PublicSignIn } from './pages/PublicSignIn';
import { SiteSigninQr } from './pages/SiteSigninQr';
import { DocumentView } from './pages/DocumentView';
import { Permits } from './pages/Permits';
import { PermitFillForm } from './pages/PermitFillForm';
import { PermitView } from './pages/PermitView';
import { useParams } from 'react-router-dom';
import { EmailConfirmed } from './pages/EmailConfirmed';
import { landedFromSignupConfirm } from './lib/supabase';
import { isNativeApp } from './lib/nativeShell';

/**
 * Decide ONCE per page load whether this browser visit is someone who
 * signed up inside the phone app and has just tapped the confirmation
 * link in their email. Those people should be sent back to the app, not
 * left in Safari/Chrome looking at the web paywall.
 *
 *  - signup_source 'app'  → always (builds 1.1.1+ set this)
 *  - signup_source 'web'  → never (web signups carry on in the browser)
 *  - no signup_source     → older app builds / older web signups: guess
 *                            from the device — phone or tablet = app user.
 */
let confirmRedirectConsumed = false;
function shouldSendBackToApp(meta: Record<string, unknown> | undefined): boolean {
  if (confirmRedirectConsumed) return false;
  confirmRedirectConsumed = true;
  if (!landedFromSignupConfirm || isNativeApp()) return false;
  const source = meta?.signup_source;
  if (source === 'app') return true;
  if (source === 'web') return false;
  try {
    const ua = navigator.userAgent || '';
    const isIpadOs = /Macintosh/.test(ua) && (navigator.maxTouchPoints ?? 0) > 1;
    return /iPhone|iPad|iPod|Android/i.test(ua) || isIpadOs;
  } catch {
    return false;
  }
}

// Legacy redirect — the original Dashboard used /sites/:siteId/incident-quick
// for a "quick log first → full report later" flow. That two-stage UX has
// been retired in favour of the single proper accident form. This redirect
// keeps any existing bookmarks / QR codes that still point at the old URL
// working — they get forwarded to /sites/:siteId/forms/accident.
function LegacyIncidentRedirect() {
  const { siteId } = useParams<{ siteId: string }>();
  return <Navigate to={`/sites/${siteId ?? ''}/forms/accident`} replace />;
}

// Catch-all 404. Rendered immediately so the user sees something rather than
// a 2-3 second blank page before a silent redirect. A plain, branded
// "page not found" panel with a "Take me home" link works for any unknown
// URL — bookmarked, shared, or typed wrong.
function NotFound() {
  return (
    <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', background: 'var(--bg, #FAF7F2)' }}>
      <div style={{ maxWidth: 420, textAlign: 'center' }}>
        <p style={{ fontSize: 48, margin: '0 0 12px', lineHeight: 1 }}>🤔</p>
        <h1 style={{ fontSize: 22, margin: '0 0 8px', fontWeight: 700, color: 'var(--ink, #0F172A)' }}>Page not found</h1>
        <p style={{ fontSize: 14, color: 'var(--ink-soft, #475569)', margin: '0 0 20px', lineHeight: 1.5 }}>
          The link you followed doesn't exist or has moved. If you got here from a bookmark or QR code, it may be out of date.
        </p>
        <a
          href="/"
          style={{ display: 'inline-block', background: 'var(--brand-deep, #0F172A)', color: '#FFFFFF', padding: '12px 24px', borderRadius: 8, textDecoration: 'none', fontWeight: 600, fontSize: 15 }}
        >
          Take me home
        </a>
      </div>
    </div>
  );
}

export default function App() {
  const { session, loading } = useSession();

  // Initialise the native (Capacitor) shell once on mount — wires the
  // Android hardware back button to React Router history, sets the
  // status-bar style to match the brand header, and hides the splash
  // screen as soon as the first render lands. No-op on the web PWA.
  useEffect(() => { initNativeShell(); }, []);

  // Same premium-feel delay as LoadingIndicator: most sessions resolve
  // under 500ms, so suppress the "Loading…" string until then. Stops
  // the 1990s-style flash on inter-page transitions (`SIGNED_IN` event
  // triggers a brief loading=true on every navigation that touches the
  // session hook).
  const showLoading = useDelayedLoading(loading);
  if (loading) {
    return (
      <div className="auth-shell">
        {showLoading && <p className="brand-tag">Loading…</p>}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Route guard pattern (introduced 11 Jun 2026)
  //
  // Every protected route now wraps its element in one of two guards rather
  // than the legacy `session ? <X /> : <Navigate to="/login" replace />`:
  //
  //   <RequireSubscription> — needs (a) session AND (b) an active subscription
  //     (see lib/billing.ts for the status semantics). This is the default for
  //     anything customer-facing — Dashboard, sites, forms, registers, etc.
  //     If they're signed in but unpaid we route them to /paywall.
  //
  //   <RequireSession> — needs a session but no subscription. Reserved for
  //     /paywall itself plus the billing-management surfaces (Settings,
  //     Plan, Security) so a customer whose subscription has lapsed can
  //     still subscribe again, see invoices, change card, or sign out.
  //
  // Why this matters: Terms of Service §3 commits us to a pay-first model. Up
  // until this change, the app only gated on `session`, so a signed-in user
  // could use every feature for free. This guard wiring is the gate that
  // delivers what the Terms promise.
  // ---------------------------------------------------------------------------

  // Signed up in the phone app, confirmed the email in the phone's browser →
  // show "Email confirmed — go back to the app" instead of the web paywall.
  if (shouldSendBackToApp(session?.user?.user_metadata)) {
    const source = session?.user?.user_metadata?.signup_source === 'app' ? '?source=app' : '';
    return <Navigate to={`/email-confirmed${source}`} replace />;
  }

  return (
    <SplashGate>
      <Routes>
        {/* Public — no session needed */}
        <Route path="/email-confirmed" element={<EmailConfirmed />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/affiliate-program" element={<PartnerProgramme />} />
        <Route path="/partner-program" element={<PartnerProgramme />} />
        <Route path="/invite/:token" element={<AcceptInvite />} />
        <Route path="/public/sign-in/:token" element={<PublicSignIn />} />
        <Route path="/login" element={session ? <Navigate to="/" replace /> : <Login />} />
        <Route path="/signup" element={session ? <Navigate to="/" replace /> : <Signup />} />

        {/* Session-only — accessible without an active subscription so the
            customer can subscribe, manage billing, or back out. */}
        <Route path="/paywall" element={<RequireSession><Paywall /></RequireSession>} />
        {/* /checkout — signed-in but NOT yet subscribed. Reads the pending
            plan the user picked on the marketing pricing page (stashed
            through signup + email verification) and redirects to Stripe.
            RequireSession only — RequireSubscription would boot them to
            /paywall since they're paying to activate the sub. */}
        <Route path="/checkout" element={<RequireSession><Checkout /></RequireSession>} />
        <Route path="/settings" element={<RequireSession><Settings /></RequireSession>} />
        <Route path="/settings/plan" element={<RequireSession><Plan /></RequireSession>} />
        <Route path="/settings/security" element={<RequireSession><Security /></RequireSession>} />
        <Route path="/terms" element={<RequireSession><Terms /></RequireSession>} />
        <Route path="/privacy" element={<RequireSession><Privacy /></RequireSession>} />

        {/* Home is the active-site Dashboard. The legacy /dashboard URL is
            kept as an alias so old bookmarks/QRs don't 404. */}
        <Route path="/" element={<RequireSubscription><Dashboard /></RequireSubscription>} />
        <Route path="/dashboard" element={session ? <Navigate to="/" replace /> : <Navigate to="/login" replace />} />
        <Route path="/welcome" element={<RequireSubscription><Welcome /></RequireSubscription>} />
        <Route path="/welcome/first-rams" element={<RequireSubscription><WelcomeFirstRams /></RequireSubscription>} />
        <Route path="/sites" element={<RequireSubscription><Sites /></RequireSubscription>} />
        <Route path="/sites/:siteId" element={<RequireSubscription><SiteRegister /></RequireSubscription>} />
        <Route path="/sites/:siteId/sign-in" element={<RequireSubscription><SignInForm /></RequireSubscription>} />
        <Route path="/sites/:siteId/checks" element={<RequireSubscription><ChecksMenu /></RequireSubscription>} />
        <Route path="/sites/:siteId/checks/:kind/run" element={<RequireSubscription><TickListRunForm /></RequireSubscription>} />
        <Route path="/sites/:siteId/checks/:kind/view" element={<RequireSubscription><TickListRunView /></RequireSubscription>} />
        {/* Legacy fallback for any old links */}
        <Route path="/sites/:siteId/checks/:kind" element={<RequireSubscription><TickListRunForm /></RequireSubscription>} />
        <Route path="/sites/:siteId/forms" element={<RequireSubscription><FormsMenu /></RequireSubscription>} />
        <Route path="/sites/:siteId/forms/history" element={<RequireSubscription><FormsHistory /></RequireSubscription>} />
        <Route path="/sites/:siteId/forms/toolbox_talk" element={<RequireSubscription><FormTBT /></RequireSubscription>} />
        <Route path="/sites/:siteId/signin-qr" element={<RequireSubscription><SiteSigninQr /></RequireSubscription>} />
        <Route path="/sites/:siteId/wrap-up" element={<RequireSubscription><FridayWrapUp /></RequireSubscription>} />
        <Route path="/sites/:siteId/forms/accident" element={<RequireSubscription><FormAccident /></RequireSubscription>} />
        {/* Legacy /incident-quick redirect — old QR codes / bookmarks still
            point here. Forward straight to the proper accident form so
            they don't 404. */}
        <Route path="/sites/:siteId/incident-quick" element={<LegacyIncidentRedirect />} />
        <Route path="/sites/:siteId/forms/subcontractor_induction" element={<RequireSubscription><FormSubbie /></RequireSubscription>} />
        <Route path="/forms/:formId" element={<RequireSubscription><FormView /></RequireSubscription>} />
        <Route path="/settings/features" element={<RequireSubscription><SettingsFeatures /></RequireSubscription>} />
        <Route path="/settings/team" element={<RequireSubscription><Team /></RequireSubscription>} />
        <Route path="/completed-jobs" element={<RequireSubscription><CompletedJobs /></RequireSubscription>} />
        <Route path="/registers" element={<RequireSubscription><Registers /></RequireSubscription>} />
        <Route path="/sites/:siteId/setup" element={<RequireSubscription><SiteSetupChecklist /></RequireSubscription>} />
        <Route path="/how-to" element={<RequireSubscription><HowToVideos /></RequireSubscription>} />
        <Route path="/sites/:siteId/records" element={<RequireSubscription><SiteRecords /></RequireSubscription>} />
        <Route path="/sites/:siteId/timesheet" element={<RequireSubscription><Timesheet /></RequireSubscription>} />
        <Route path="/sites/:siteId/rams" element={<RequireSubscription><RamsList /></RequireSubscription>} />
        <Route path="/sites/:siteId/rams/list" element={<RequireSubscription><RamsListView /></RequireSubscription>} />
        <Route path="/sites/:siteId/rams/new" element={<RequireSubscription><RamsAdd /></RequireSubscription>} />
        <Route path="/sites/:siteId/rams/presets" element={<RequireSubscription><RamsPresets /></RequireSubscription>} />
        <Route path="/sites/:siteId/rams/preset/:templateId" element={<RequireSubscription><RamsPresetForm /></RequireSubscription>} />
        <Route path="/registers/plant" element={<RequireSubscription><RegisterPlant /></RequireSubscription>} />
        <Route path="/registers/coshh" element={<RequireSubscription><RegisterCOSHH /></RequireSubscription>} />
        <Route path="/registers/training" element={<RequireSubscription><RegisterTraining /></RequireSubscription>} />
        <Route path="/registers/subbies" element={<RequireSubscription><RegisterSubbies /></RequireSubscription>} />
        <Route path="/registers/expiries" element={<RequireSubscription><RegisterExpiries /></RequireSubscription>} />
        <Route path="/registers/first-aiders" element={<RequireSubscription><RegisterFirstAiders /></RequireSubscription>} />

        {/* Documents library */}
        <Route path="/documents" element={<RequireSubscription><DocumentsLibrary /></RequireSubscription>} />
        <Route path="/documents/issued" element={<RequireSubscription><DocumentsIssued /></RequireSubscription>} />
        <Route path="/documents/:docId" element={<RequireSubscription><DocumentView /></RequireSubscription>} />
        <Route path="/documents/org/template/:templateId" element={<RequireSubscription><DocumentFillForm /></RequireSubscription>} />
        <Route path="/documents/site/:siteId/template/construction_phase_plan" element={<RequireSubscription><CppFillForm /></RequireSubscription>} />
        <Route path="/documents/site/:siteId/template/:templateId" element={<RequireSubscription><DocumentFillForm /></RequireSubscription>} />

        {/* Permits */}
        <Route path="/sites/:siteId/close" element={<RequireSubscription><CloseSite /></RequireSubscription>} />
        <Route path="/sites/:siteId/issues" element={<RequireSubscription><OpenIssues /></RequireSubscription>} />
        <Route path="/sites/:siteId/permits" element={<RequireSubscription><Permits /></RequireSubscription>} />
        <Route path="/sites/:siteId/permits/new/:templateId" element={<RequireSubscription><PermitFillForm /></RequireSubscription>} />
        <Route path="/permits/:permitId" element={<RequireSubscription><PermitView /></RequireSubscription>} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </SplashGate>
  );
}
